import { type KnownEventType } from '@apps/web/features/phoebe_chat_v3/model/event_types';
import {
    type RegistryDefinitionShape,
    type RendererOverlay,
    type RendererRegistry,
    type RendererRegistryShape,
} from '@apps/web/features/phoebe_chat_v3/registry/types';

export type DefinitionModuleShape = Readonly<
    Partial<{
        readonly [K in KnownEventType]: RegistryDefinitionShape;
    }>
>;

type ModuleKey<Module> = Module extends DefinitionModuleShape
    ? Extract<keyof Module, KnownEventType>
    : never;

type UnknownModuleKey<Module> = Module extends DefinitionModuleShape
    ? Exclude<keyof Module, KnownEventType>
    : never;

type DefinitionForKey<
    Module,
    K extends KnownEventType,
> = Module extends DefinitionModuleShape
    ? K extends keyof Module
        ? Module[K]
        : never
    : never;

type DuplicateModuleKeys<
    Modules extends ReadonlyArray<DefinitionModuleShape>,
    Seen extends KnownEventType = never,
> = Modules extends readonly [
    infer Head extends DefinitionModuleShape,
    ...infer Tail extends ReadonlyArray<DefinitionModuleShape>,
]
    ?
          | Extract<ModuleKey<Head>, Seen>
          | DuplicateModuleKeys<Tail, Seen | ModuleKey<Head>>
    : never;

type ModuleValidation<Modules extends ReadonlyArray<DefinitionModuleShape>> =
    number extends Modules['length']
        ? Readonly<{
              readonly readonlyDefinitionModuleTupleRequired: true;
          }>
        : Exclude<KnownEventType, ModuleKey<Modules[number]>> extends never
          ? UnknownModuleKey<Modules[number]> extends never
              ? DuplicateModuleKeys<Modules> extends never
                  ? unknown
                  : Readonly<{
                        readonly duplicateRendererEventTypes: DuplicateModuleKeys<Modules>;
                    }>
              : Readonly<{
                    readonly unknownRendererEventTypes: UnknownModuleKey<
                        Modules[number]
                    >;
                }>
          : Readonly<{
                readonly missingRendererEventTypes: Exclude<
                    KnownEventType,
                    ModuleKey<Modules[number]>
                >;
            }>;

export type ComposedRendererRegistry<
    Modules extends ReadonlyArray<DefinitionModuleShape>,
> = Readonly<{
    readonly [K in KnownEventType]: DefinitionForKey<Modules[number], K>;
}>;

export type V3RendererRegistry<
    Modules extends ReadonlyArray<DefinitionModuleShape> | null = null,
> =
    Modules extends ReadonlyArray<DefinitionModuleShape>
        ? ComposedRendererRegistry<Modules>
        : RendererRegistryShape;

export function composeDefinitionModules<
    const Modules extends ReadonlyArray<DefinitionModuleShape>,
>(
    generatedDefinitionModules: Modules & ModuleValidation<Modules>,
): ComposedRendererRegistry<Modules> {
    const definitions = new Map<KnownEventType, RegistryDefinitionShape>();
    for (const module of generatedDefinitionModules) {
        for (const [eventType, definition] of Object.entries(module)) {
            if (definitions.has(eventType as KnownEventType)) {
                throw new Error(
                    `Duplicate renderer definition event type: ${eventType}`,
                );
            }
            definitions.set(eventType as KnownEventType, definition);
        }
    }
    return Object.fromEntries(definitions) as ComposedRendererRegistry<Modules>;
}

export function composeRendererRegistry<R extends RendererRegistryShape>(
    base: RendererRegistry<R>,
    overlay: RendererOverlay<R>,
): RendererRegistry<R> {
    for (const eventType of Object.keys(overlay)) {
        if (!Object.prototype.hasOwnProperty.call(base, eventType)) {
            throw new Error(
                `Unknown renderer overlay event type: ${eventType}`,
            );
        }
    }
    return { ...base, ...overlay };
}
